import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Skeleton,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import { useCombobox } from "downshift";
import _ from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import { useSearchParents } from "src/generated/hooks/form-service/queries";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { useFocus } from "src/hooks/useFocus";
import { canonicalPhoneNumber, formatUSPhoneNumber } from "src/services/format";
import { isValidEmail, isValidPhoneNumber } from "src/services/formValidations";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";

const PAGE_SIZE = 5;
const SEARCH_DEBOUNCE_MS = 350;
// Stable default so an omitted excludeIds prop does not change identity per render.
const NO_EXCLUDED_IDS: string[] = [];

// Suggestions fire only once the value is a complete email or phone number, not
// on partial input.
function isCompleteContact(value: string): boolean {
  return isValidEmail(value) || isValidPhoneNumber(value);
}

// Treat input with no letters or "@" as a phone number so it can be canonicalized
// and formatted; anything with letters or "@" is an email and kept verbatim.
function isPhoneLike(value: string): boolean {
  return value.length > 0 && !/[a-zA-Z@]/.test(value);
}

// What the user sees: a phone is shown formatted, an email verbatim.
function displayContact(value: string): string {
  return isPhoneLike(value) ? formatUSPhoneNumber(value) : value;
}

interface ParentGuardianSelectInputProps {
  organizationId: string;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  onSelectedParent?: (value: ParentSummary | null | undefined) => void;
  // Called when the create-new row is clicked, with the current raw search text.
  // Left as a no-op by default; the host wires this to whatever it needs (later
  // it will prepare the guardian payload for the create-student flow).
  onCreateNew?: (searchValue: string) => void;
  // Person ids already linked (e.g. guardians already on the student). Shown in
  // the results but disabled/unselectable, so the admin sees they exist instead of
  // recreating them.
  excludeIds?: string[];
}

function toParentName(
  parent: ParentSummary | null | undefined,
  defaultValue: string = ""
): string {
  if (!parent) return defaultValue;
  const parts = [parent.first_name, parent.last_name].filter(Boolean);
  return parts.join(" ") || defaultValue;
}

export function ParentGuardianSelectInput({
  organizationId,
  onSelectedParent,
  onCreateNew,
  placeholder = "E.g: dandoe@email.com or +1 (202) 123-456",
  label = "Search by email address or phone number",
  isDisabled = false,
  excludeIds = NO_EXCLUDED_IDS,
}: ParentGuardianSelectInputProps) {
  const outsideRef = React.useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = React.useState<string>("");
  const [parents, setParents] = React.useState<ParentSummary[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const searchValue = inputValue.trim();

  const [debouncedSearchValue, setDebouncedSearchValue] =
    React.useState(searchValue);
  const debouncedSetSearchValue = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearchValue(searchValue);
    return () => debouncedSetSearchValue.cancel();
  }, [searchValue, debouncedSetSearchValue]);

  // Canonicalize a phone to E.164 only for the request; the input keeps its
  // formatted display value untouched.
  const searchTerm = isPhoneLike(debouncedSearchValue)
    ? canonicalPhoneNumber(debouncedSearchValue)
    : debouncedSearchValue;

  const {
    data: searchResponse,
    isLoading,
    isError: isSearchError,
  } = useSearchParents(
    {
      query: {
        organizationId,
        search: searchTerm,
        limit: PAGE_SIZE,
        offset,
      },
    },
    undefined,
    {
      enabled: !!organizationId && isCompleteContact(debouncedSearchValue),
    }
  );
  const data = searchResponse?.data;

  useOutsideClick({
    ref: outsideRef,
    handler: () => closeMenu(),
  });

  React.useEffect(() => {
    if (!isCompleteContact(debouncedSearchValue)) {
      setParents([]);
      setTotal(0);
      setOffset(0);
      return;
    }
    if (!data) return;
    if (offset === 0) {
      setParents(data.parents);
    } else {
      setParents((prev) => _.unionBy(prev, data.parents, "id"));
    }
    setTotal(data.total);
  }, [data, offset, debouncedSearchValue]);

  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearchValue]);

  const fetchMore = React.useCallback(() => {
    if (parents.length < total && isCompleteContact(searchValue)) {
      setOffset(parents.length);
    }
  }, [parents.length, total, searchValue]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoading && parents.length > 0) {
          fetchMore();
        }
      },
      { rootMargin: "20px" }
    );
    const current = observerRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [parents, isLoading, fetchMore]);

  const [isFocused, setIsFocused] = React.useState(false);
  const [inputRef, setInputFocus] = useFocus();
  React.useEffect(() => {
    if (isFocused) setInputFocus();
  }, [setInputFocus, isFocused]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.onblur = () => setIsFocused(false);
    }
  }, [inputRef, isFocused]);

  // Selecting a parent emits it to the host (which owns the chip list) and clears
  // the search so the field is ready for the next lookup.
  const handleSelect = (parent: ParentSummary) => {
    onSelectedParent?.(parent);
    setInputValue("");
    setDebouncedSearchValue("");
    setParents([]);
    setOffset(0);
    closeMenu();
  };

  const handleCreateNew = () => {
    closeMenu();
    onCreateNew?.(inputValue);
  };

  // Already-linked people (e.g. guardians already on the student) stay in the
  // results but are shown disabled, so an admin sees they exist and does not try
  // to recreate them.
  const excludedSet = React.useMemo(() => new Set(excludeIds), [excludeIds]);

  const noResults =
    isCompleteContact(debouncedSearchValue) &&
    !isLoading &&
    data !== undefined &&
    parents.length === 0;

  // The create-new action only surfaces once a search has actually resolved for
  // the current query (results or no results), never while idle or loading.
  const searchAttempted =
    isCompleteContact(debouncedSearchValue) && !isLoading && data !== undefined;

  // True from the moment a complete email/phone is typed until its results
  // resolve: covers both the debounce window (input not yet caught up) and the
  // in-flight query. Drives the loading feedback so the field never looks dead.
  const isPending =
    isCompleteContact(searchValue) &&
    (searchValue !== debouncedSearchValue || isLoading);

  // The user has typed something, but it is not yet a complete email or phone, so
  // no search fires. Show a hint explaining why rather than an empty dropdown.
  const isPartialContact =
    searchValue.length > 0 && !isCompleteContact(searchValue);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setHighlightedIndex,
  } = useCombobox<ParentSummary>({
    items: parents,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: toParentName,
    onInputValueChange({ inputValue: newValue }) {
      if (newValue !== inputValue) {
        setInputValue(newValue ?? "");
        setOffset(0);
      }
    },
    stateReducer(state, actionAndChanges) {
      const { changes, type, index } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const newIndex =
            index !== undefined && index >= 0
              ? index
              : changes.highlightedIndex !== undefined &&
                changes.highlightedIndex >= 0
              ? changes.highlightedIndex
              : undefined;
          const candidate = newIndex !== undefined ? parents[newIndex] : null;
          // Never select an already-linked (disabled) row.
          const newSelectedItem =
            candidate && excludedSet.has(candidate.id) ? null : candidate;
          return { ...changes, selectedItem: newSelectedItem };
        }
        default:
          return changes;
      }
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) handleSelect(selectedItem);
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) openMenu();
  }, [isDisabled, openMenu]);

  const markdownName = React.useCallback(
    (displayName: string) => {
      if (!inputValue) return displayName;
      const escaped = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "ig");
      return displayName.replace(regex, "**$1**");
    },
    [inputValue]
  );

  const renderItem = (parent: ParentSummary, isLinked: boolean) => {
    const displayName = toParentName(parent);
    const name = markdownName(displayName);
    return (
      <Flex direction="row" gap={3} width="100%" justify="space-between">
        <Flex direction="column" gap={0}>
          <Text fontSize="md">
            <Markdown>{name}</Markdown>
          </Text>
          {parent.email_address && (
            <Text fontSize="sm">{parent.email_address}</Text>
          )}
          {parent.phone_number && (
            <Text fontSize="sm">
              {formatUSPhoneNumber(parent.phone_number)}
            </Text>
          )}
        </Flex>
        {isLinked && (
          <Flex align="center" whiteSpace="nowrap">
            <Text fontSize="sm" color="gray.500" fontWeight="600">
              Already linked
            </Text>
          </Flex>
        )}
      </Flex>
    );
  };

  const renderLoadingItem = () => (
    <Flex direction="column" gap={0} width="100%">
      <Skeleton height="1.25rem" width="60%" />
      <Skeleton height="1rem" width="40%" marginTop="0.5rem" />
    </Flex>
  );

  return (
    <Box position="relative" width="100%" ref={outsideRef}>
      <FormControl>
        <FormLabel {...getLabelProps()}>
          <Glossary>{label}</Glossary>
        </FormLabel>
        <InputGroup
          onClick={openMenuUnlessDisabled}
          onFocus={openMenuUnlessDisabled}
          display="flex"
          justifyContent="center"
        >
          <InputLeftElement children={<SearchIcon color="gray.500" />} />
          <Input
            {...getInputProps(
              {
                ref: inputRef,
                // Store a phone canonically and an email verbatim, synchronously
                // on change. Storing canonically means the search matches the
                // backend's canonical numbers and transmits in E.164. downshift
                // fires onInputValueChange a tick later, so setting it here also
                // keeps the controlled value from lagging a commit.
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = event.target.value;
                  if (raw === "+") {
                    setInputValue("");
                    return;
                  }
                  setInputValue(
                    isPhoneLike(raw) ? canonicalPhoneNumber(raw) : raw
                  );
                },
              },
              { suppressRefError: true }
            )}
            {...PASSWORD_MANAGER_IGNORE}
            value={displayContact(inputValue)}
            placeholder={placeholder}
            isDisabled={isDisabled}
          />
        </InputGroup>
        <UnorderedList
          {...getMenuProps({}, { suppressRefError: true })}
          display={isOpen ? "block" : "none"}
          position="absolute"
          background="white"
          marginLeft={0}
          zIndex="2000"
          boxShadow="lg"
          width="100%"
          marginTop={1}
          overflow="auto"
          maxHeight="300px"
        >
          {isPartialContact && (
            <ListItem textAlign="center" p={3}>
              <Text color="gray.500" fontSize="md">
                Enter a full email or phone number to search.
              </Text>
            </ListItem>
          )}
          {isPending && (
            <ListItem textAlign="center" p={2}>
              <Flex align="center" minHeight="16" padding="4">
                {renderLoadingItem()}
              </Flex>
            </ListItem>
          )}
          {!isPartialContact &&
            !isPending &&
            !noResults &&
            parents.map((parent, index) => {
              const isLinked = excludedSet.has(parent.id);
              return (
                <ListItem
                  {...getItemProps({ item: parent, index, disabled: isLinked })}
                  key={parent.id}
                  backgroundColor={
                    highlightedIndex === index ? "blackAlpha.100" : "white"
                  }
                  listStyleType="none"
                  cursor={isLinked ? "not-allowed" : undefined}
                  opacity={isLinked ? 0.6 : 1}
                  aria-disabled={isLinked}
                >
                  <Flex align="center" minHeight="16" padding="4" width="100%">
                    {renderItem(parent, isLinked)}
                  </Flex>
                </ListItem>
              );
            })}
          {!isPending && noResults && !isSearchError && (
            <ListItem textAlign="center" p={3}>
              <Text color="gray.500" fontSize="md">
                <Glossary>No parents or guardians found</Glossary>
              </Text>
            </ListItem>
          )}
          {!isPending && isSearchError && (
            <ListItem textAlign="center" p={3}>
              <Text color="red.600" fontSize="md">
                Something went wrong searching. Please try again.
              </Text>
            </ListItem>
          )}
          {!isPending && searchAttempted && !isSearchError && (
            <ListItem
              borderTop="1px solid"
              borderColor="gray.100"
              listStyleType="none"
            >
              <Flex
                align="center"
                gap={2}
                padding="4"
                color="primary.500"
                cursor="pointer"
                _hover={{ backgroundColor: "blackAlpha.100" }}
                onMouseMove={() => setHighlightedIndex(-1)}
                onClick={handleCreateNew}
              >
                <RiAddLine size="1.25rem" />
                <Text fontSize="md" fontWeight="700">
                  <Glossary>Create new parent/guardian</Glossary> "
                  {displayContact(inputValue)}"
                </Text>
              </Flex>
            </ListItem>
          )}
          <Box ref={observerRef} height="5px" />
        </UnorderedList>
      </FormControl>
    </Box>
  );
}
